import React, { memo, useMemo } from 'react';

import { Box } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  CHANGES_CARRIED_TITLE,
  FEE_CHANGES_CARRIED_TITLE,
  NO_CARRIED_CHANGES_MESSAGE,
  REMOVED_AT_AGGREGATION_TITLE,
  REMOVED_DESCRIPTION,
} from '../../../../LoanCicHistory.constants';
import { cicChangePropTypes, cicFeeChangePropTypes } from '../../../../LoanCicHistory.propTypes';
import { LoanCicHistoryTestIds } from '../../../../LoanCicHistory.testIds';
import { toCarriedChange } from '../../../../LoanCicHistory.utils';
import CicChangesTable from '../../../CicChangesTable';
import CicEmptyMessage from '../../../CicEmptyMessage';
import CicFeeChangesTable from '../../../CicFeeChangesTable';
import CicSectionCard from '../../../CicSectionCard';

import { DISCLOSED_CHIP, NOT_DISCLOSED_CHIP } from './CicDisclosureDetails.constants';
import { detailsStyles } from './CicDisclosureDetails.styles';

const CicDisclosureDetails = ({ disclosure, isEditAllowed, onEditFeeChange, onOpenHistory }) => {
  const { formDeliveryRequestId, carried, removed, feeChanges } = disclosure;

  const carriedChanges = useMemo(() => carried.map(toCarriedChange), [carried]);

  const hasCarriedChanges = !_.isEmpty(carriedChanges);
  const hasFeeChanges = !_.isEmpty(feeChanges);

  return (
    <Box sx={detailsStyles}>
      {(hasCarriedChanges || !hasFeeChanges) && (
        <CicSectionCard
          chip={DISCLOSED_CHIP}
          dataTestId={LoanCicHistoryTestIds.getDisclosureCarriedCardId(formDeliveryRequestId)}
          title={CHANGES_CARRIED_TITLE}
        >
          {hasCarriedChanges ? (
            <CicChangesTable changes={carriedChanges} onOpenHistory={onOpenHistory} />
          ) : (
            <CicEmptyMessage message={NO_CARRIED_CHANGES_MESSAGE} />
          )}
        </CicSectionCard>
      )}

      {hasFeeChanges && (
        <CicSectionCard
          chip={DISCLOSED_CHIP}
          dataTestId={LoanCicHistoryTestIds.getDisclosureFeeChangesCardId(formDeliveryRequestId)}
          title={FEE_CHANGES_CARRIED_TITLE}
        >
          <CicFeeChangesTable
            feeChanges={feeChanges}
            isEditAllowed={isEditAllowed}
            onEdit={onEditFeeChange}
          />
        </CicSectionCard>
      )}

      {!_.isEmpty(removed) && (
        <CicSectionCard
          chip={NOT_DISCLOSED_CHIP}
          dataTestId={LoanCicHistoryTestIds.getDisclosureRemovedCardId(formDeliveryRequestId)}
          description={REMOVED_DESCRIPTION}
          title={REMOVED_AT_AGGREGATION_TITLE}
        >
          <CicChangesTable changes={removed} onOpenHistory={onOpenHistory} />
        </CicSectionCard>
      )}
    </Box>
  );
};

CicDisclosureDetails.propTypes = {
  disclosure: PropTypes.shape({
    formDeliveryRequestId: PropTypes.string.isRequired,
    carried: PropTypes.arrayOf(
      PropTypes.shape({
        change: cicChangePropTypes.isRequired,
        field: PropTypes.string,
        label: PropTypes.string,
        previousDisplay: PropTypes.string,
        newDisplay: PropTypes.string,
      }),
    ).isRequired,
    removed: PropTypes.arrayOf(cicChangePropTypes).isRequired,
    feeChanges: PropTypes.arrayOf(cicFeeChangePropTypes),
  }).isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  onEditFeeChange: PropTypes.func.isRequired,
  onOpenHistory: PropTypes.func.isRequired,
};

export default memo(CicDisclosureDetails);
